import React from 'react';

/**
 * Route-level error boundary for the Business shell.
 *
 * Before this, the app had NO error boundary anywhere in the tree, so ANY render throw — a
 * temporal-dead-zone ReferenceError, or React error #31 from a structured object rendered as a JSX
 * child — unmounted the whole React tree and produced a blank white page (see P0-B / P0-C).
 *
 * This boundary contains the failure to the routed content area: it renders a safe, friendly retry
 * state instead of white-paging the shell, logs a REDACTED diagnostic to the console for engineers
 * (never shown to the user; no raw stack in the UI), and auto-resets when the route changes
 * (`resetKey`) so navigating to another page always recovers. It does NOT hide the underlying defect —
 * the error is still surfaced to the console for diagnosis.
 */
class BusinessRouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, ref: null, _resetKey: props.resetKey };
  }

  static getDerivedStateFromError() {
    // Short correlation reference the user can quote in a report without exposing any internals.
    const ref = 'ERR-' + Math.random().toString(36).slice(2, 8).toUpperCase();
    return { hasError: true, ref };
  }

  static getDerivedStateFromProps(props, state) {
    // Auto-recover when the route changes: leaving the broken page clears the error.
    if (props.resetKey !== state._resetKey) {
      return { hasError: false, ref: null, _resetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error, info) {
    // Engineer-only diagnostic. Bounded, never rendered, never a raw stack in the UI.
    try {
      const firstFrame = (info && info.componentStack ? info.componentStack.split('\n')[1] : '') || '';
      // eslint-disable-next-line no-console
      console.error('[BusinessRouteError]', this.state.ref,
        (error && error.name) || 'Error', (error && error.message) || '', firstFrame.trim());
    } catch { /* never let logging throw */ }
  }

  handleRetry = () => this.setState({ hasError: false, ref: null });

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex-1 min-h-0 flex items-center justify-center p-8">
          <div className="max-w-md w-full text-center rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900">Something went wrong loading this page</h2>
            <p className="mt-2 text-sm text-gray-600">
              We couldn't display this section right now. You can try again, or pick another page from the menu.
            </p>
            {this.state.ref && (
              <p className="mt-3 text-xs text-gray-400">Reference: {this.state.ref}</p>
            )}
            <button
              type="button"
              onClick={this.handleRetry}
              className="mt-5 inline-flex items-center justify-center rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default BusinessRouteErrorBoundary;
